// CookChatSheet — in-cook Q&A surface for "ASK ABOUT THIS STEP."
//
// Scoped per-step: opens onto whatever step the user is on, the
// transcript clears when activeStep changes (parent unmounts/remounts
// at the chip level via `key={step.id}`). Quick-question chips are
// static for v1 (Is it done? / Why this temp? / Substitute?). The
// underlying cook page stays visible behind a dimmed backdrop so the
// timer + step text remain in peripheral vision while reading the
// answer.
//
// Egress: never auto-fires. The first network call happens only when
// the user taps a chip or sends a typed question.

import { useEffect, useRef, useState } from "react";
import ModalSheet from "./ModalSheet";
import { COLOR, FONT, RADIUS, Z } from "../lib/tokens";
import { askCookChat } from "../lib/askCookChat";
import { useIngredientInfo } from "../lib/useIngredientInfo";

const QUICK_QUESTIONS = [
  "Is it done?",
  "Why this temp?",
  "Substitute?",
];

export default function CookChatSheet({ recipe, step, onClose }) {
  const { dbMap } = useIngredientInfo();
  const [draft, setDraft] = useState("");
  const [thread, setThread] = useState([]); // [{ q, a, cached?, error? }]
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  // Auto-focus the input on mount so a typed question is one fewer tap.
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Auto-scroll to the bottom of the thread when answers land.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, busy]);

  const send = async (text) => {
    const q = (text || draft).trim();
    if (!q || busy) return;
    setBusy(true);
    setDraft("");
    setThread((t) => [...t, { q, a: null }]);
    const priorTurns = thread
      .filter((t) => t.a && !t.error)
      .map((t) => ({ q: t.q, a: t.a }));
    const { answer, cached, error } = await askCookChat({
      recipe, step, question: q, priorTurns, dbInfoMap: dbMap,
    });
    setThread((t) => {
      const next = [...t];
      const last = next[next.length - 1];
      if (last && last.q === q) {
        if (error) next[next.length - 1] = { q, a: null, error };
        else       next[next.length - 1] = { q, a: answer, cached };
      }
      return next;
    });
    setBusy(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const stepLabelNum = String(step?.id || "").replace(/^step/i, "").trim() || "—";

  return (
    <ModalSheet
      onClose={onClose}
      zIndex={Z.sheet}
      maxHeight="55vh"
      label="ASK ABOUT THIS STEP"
      scrollRef={scrollRef}
    >
      <div style={{ padding: "4px 22px 18px", display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
        <div style={{
          fontFamily: FONT.itemTitle, fontStyle: "italic",
          fontSize: 18, lineHeight: 1.2, color: COLOR.ink,
        }}>
          Step {stepLabelNum}{step?.title ? ` — ${step.title}` : ""}
        </div>

        {/* Quick-question chips. Hidden once the user starts a thread
            so they don't crowd the answers. */}
        {thread.length === 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {QUICK_QUESTIONS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => send(q)}
                disabled={busy}
                className="mcm-focusable"
                style={{
                  fontFamily: FONT.forma, fontSize: 12, fontWeight: 500,
                  color: COLOR.ink,
                  background: COLOR.ground,
                  border: `1px solid ${COLOR.border}`,
                  borderRadius: RADIUS.pill,
                  padding: "7px 12px",
                  cursor: busy ? "default" : "pointer",
                  opacity: busy ? 0.55 : 1,
                  touchAction: "manipulation",
                }}
              >
                {q}
              </button>
            ))}
          </div>
        )}

        <div
          ref={scrollRef}
          style={{
            flex: 1, overflowY: "auto", minHeight: 0,
            display: "flex", flexDirection: "column", gap: 10,
            paddingRight: 2,
          }}
        >
          {thread.map((turn, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{
                alignSelf: "flex-end", maxWidth: "85%",
                background: COLOR.goldDeep,
                border: `1px solid ${COLOR.goldDim}`,
                borderRadius: RADIUS.lg,
                padding: "8px 12px",
                fontFamily: FONT.forma, fontSize: 14, color: COLOR.ink,
                lineHeight: 1.4,
              }}>
                {turn.q}
              </div>
              <div style={{
                alignSelf: "flex-start", maxWidth: "92%",
                background: COLOR.ground,
                border: `1px solid ${COLOR.border}`,
                borderRadius: RADIUS.lg,
                padding: "10px 13px",
                fontFamily: FONT.forma, fontSize: 14, color: COLOR.ink,
                lineHeight: 1.45,
              }}>
                {turn.a == null && !turn.error && (
                  <span style={{ color: COLOR.muted, fontStyle: "italic" }}>thinking…</span>
                )}
                {turn.error && (
                  <span style={{ color: COLOR.rose }}>{turn.error}</span>
                )}
                {turn.a && (
                  <>
                    {turn.a}
                    {turn.cached && (
                      <div style={{
                        marginTop: 6,
                        fontFamily: FONT.mono, fontSize: 9,
                        color: COLOR.sub, letterSpacing: "0.1em",
                      }}>
                        FROM CACHE
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={onSubmit} style={{ display: "flex", gap: 8 }}>
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Ask about this step…"
            disabled={busy}
            style={{
              flex: 1,
              fontFamily: FONT.forma, fontSize: 14, color: COLOR.ink,
              background: COLOR.ground,
              border: `1px solid ${COLOR.border}`,
              borderRadius: RADIUS.lg,
              padding: "10px 12px",
              outline: "none",
            }}
          />
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            className="mcm-focusable"
            style={{
              fontFamily: FONT.mono, fontSize: 10, fontWeight: 600,
              letterSpacing: "0.12em",
              color: draft.trim() ? COLOR.ink : COLOR.sub,
              background: draft.trim() ? COLOR.gold : COLOR.ground,
              border: `1px solid ${draft.trim() ? COLOR.goldDim : COLOR.border}`,
              borderRadius: RADIUS.lg,
              padding: "10px 14px",
              cursor: busy || !draft.trim() ? "default" : "pointer",
              touchAction: "manipulation",
            }}
          >
            ASK
          </button>
        </form>
      </div>
    </ModalSheet>
  );
}
